/**
 * Formatting utilities for Strava activity display.
 */

const PACE_SPORTS = new Set(["Run", "Walk", "Hike", "TrailRun"]);

/** Whether an activity type uses pace (min/km) rather than speed (km/h). */
export function isPaceSport(sportType: string): boolean {
  return PACE_SPORTS.has(sportType);
}

/** Format meters to human-readable distance: "10.5 km" or "850 m" */
export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
}

/** Format seconds to duration string: "52:30" or "1:30:00" */
export function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/** Format pace as "5:00 /km" from distance (m) and time (s). Returns "--" for zero distance. */
export function formatPace(distanceMeters: number, timeSeconds: number): string {
  if (distanceMeters <= 0 || timeSeconds <= 0) {
    return "--";
  }
  const paceSecondsPerKm = timeSeconds / (distanceMeters / 1000);
  const paceMins = Math.floor(paceSecondsPerKm / 60);
  const paceSecs = Math.floor(paceSecondsPerKm % 60);
  return `${paceMins}:${String(paceSecs).padStart(2, "0")} /km`;
}

/** Format speed as "30.0 km/h" from distance (m) and time (s). Returns "--" for zero time. */
export function formatSpeed(distanceMeters: number, timeSeconds: number): string {
  if (distanceMeters <= 0 || timeSeconds <= 0) {
    return "--";
  }
  const kmh = distanceMeters / 1000 / (timeSeconds / 3600);
  return `${kmh.toFixed(1)} km/h`;
}

const SPORT_ICONS: Record<string, string> = {
  Run: "\u{1F3C3}",
  Ride: "\u{1F6B4}",
  Swim: "\u{1F3CA}",
  Walk: "\u{1F6B6}",
  Hike: "\u{26F0}\uFE0F",
  AlpineSki: "\u{26F7}\uFE0F",
  NordicSki: "\u{26F7}\uFE0F",
  Snowboard: "\u{1F3C2}",
  Rowing: "\u{1F6A3}",
  Kayaking: "\u{1F6F6}",
  Yoga: "\u{1F9D8}",
  WeightTraining: "\u{1F3CB}\uFE0F",
  Workout: "\u{1F4AA}",
};

/** Map Strava sport_type to an emoji icon. Returns a generic activity icon for unknown types. */
export function sportIcon(sportType: string): string {
  return SPORT_ICONS[sportType] ?? "\u{1F3C5}";
}

/** Format ISO date to localized full date: "Sunday, March 23, 2026" */
export function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Format ISO date to relative: "2h ago", "yesterday", "Mar 15" */
export function formatRelativeDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  const month = date.toLocaleString(undefined, { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  if (year === now.getFullYear()) {
    return `${month} ${day}`;
  }
  return `${month} ${day}, ${year}`;
}
